if (!customElements.get('cart-upsell')) {
  customElements.define('cart-upsell', class extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();
      this.addEventListener('click', this.onClick.bind(this), { signal: this.abortController.signal });
      this.addEventListener('shopify:block:select', () => this.closest('cart-drawer')?.show(), { signal: this.abortController.signal });
      this.selectFlavor(this.dataset.selectedFlavor);
    }

    disconnectedCallback() { this.abortController.abort(); }

    selectFlavor(flavor) {
      this.selected = Array.from(this.querySelectorAll('[data-flavor]')).find(button => button.dataset.flavor === flavor && !button.disabled);
      this.querySelectorAll('[data-flavor]').forEach(button => button.setAttribute('aria-pressed', String(button === this.selected)));
      this.querySelectorAll('[data-flavor-image], [data-flavor-price]').forEach(panel => {
        panel.hidden = (panel.dataset.flavorImage || panel.dataset.flavorPrice) !== this.selected?.dataset.flavor;
      });
      this.querySelector('[data-add]').disabled = !this.selected || this.dataset.configured !== 'true';
    }

    onClick(event) {
      if (this.busy) return;
      const flavorButton = event.target.closest('[data-flavor]');
      if (flavorButton && !flavorButton.disabled) this.selectFlavor(flavorButton.dataset.flavor);
      if (event.target.closest('[data-add]')) this.addToCart();
    }

    async request(url, body) {
      const method = body === undefined ? 'GET' : 'POST';
      const response = await fetch(url, {
        ...theme.utils.fetchConfig('json', method),
        ...(body === undefined ? {cache: 'no-store'} : {body: JSON.stringify(body)})
      });
      const result = await response.json();
      if (!response.ok || result.status || result.errors) {
        throw new Error(result.description || this.dataset.error);
      }
      return result;
    }

    codes(cart) {
      if (Array.isArray(cart.discount_codes)) return cart.discount_codes.map(discount => discount.code).filter(Boolean);
      return Array.from(document.querySelectorAll('[is="discount-remove"][data-discount]')).map(button => button.dataset.discount).filter(Boolean);
    }

    hasUpsell(cart) {
      return cart.items.some(item => item.properties?._slamit_upsell === 'drawer'
        || String(item.product_id) === this.dataset.productId);
    }

    discountVerified(cart, attemptId, code, previousCart) {
      const item = cart.items.find(line => line.properties?._slamit_upsell_id === attemptId);
      if (!item) return false;
      if (item.quantity !== 1) return false;
      if (String(item.product_id) !== this.dataset.productId) return false;
      const percentage = Number(this.dataset.percentage);
      if (!percentage) return true;
      const normalize = value => String(value).toLowerCase();
      const isOffer = application => typeof application?.title === 'string'
        && (application.title === 'Slamit drawer upsell' || normalize(application.title) === normalize(code));
      if (!cart.discount_codes?.some(discount => normalize(discount.code) === normalize(code) && discount.applicable === true)) return false;
      const previousCodes = previousCart.discount_codes?.filter(discount => discount.applicable === true) || [];
      const displaced = previousCodes.filter(previous => !cart.discount_codes.some(discount => normalize(discount.code) === normalize(previous.code) && discount.applicable === true));
      if (displaced.length) return false;
      const otherLine = cart.items.find(line => line !== item && (
        line.line_level_discount_allocations?.some(discount => isOffer(discount.discount_application) && Number(discount.amount) > 0)
        || line.discounts?.some(discount => isOffer(discount) && Number(discount.amount) > 0)
      ));
      if (otherLine) return false;
      if (cart.cart_level_discount_applications?.some(discount => isOffer(discount) && Number(discount.total_allocated_amount) > 0)) return false;
      const allocation = item.line_level_discount_allocations?.find(discount => isOffer(discount.discount_application));
      const expected = Math.round(item.original_price * percentage / 100);
      if (expected <= 0) return false;
      if (!allocation) return false;
      if (!(Math.abs(Number(allocation.amount) - expected) <= 1)) return false;
      return true;
    }

    async refreshCart(cart, sectionIds) {
      if (sectionIds.some(id => !cart.sections?.[id])) {
        const url = new URL(theme.routes.root_url, window.location.origin);
        url.searchParams.set('sections', sectionIds.join(','));
        const response = await fetch(url, {cache: 'no-store'});
        if (!response.ok) throw new Error(this.dataset.refreshError);
        cart.sections = await response.json();
        if (sectionIds.some(id => !cart.sections[id])) throw new Error(this.dataset.refreshError);
      }
      theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-upsell', productVariantId: this.selected.dataset.variantId, cart });
    }

    async rollback(attemptId, discountChanged, previousCodes, sectionIds) {
      let cart = await this.request(theme.routes.cart_url);
      const item = cart.items.find(line => line.properties?._slamit_upsell_id === attemptId);
      if (item) cart = await this.request(theme.routes.cart_change_url, {id: item.key, quantity: 0});
      if (discountChanged) {
        const code = this.dataset.discountCode.toLowerCase();
        const codes = [...new Set([...previousCodes, ...this.codes(cart).filter(value => value.toLowerCase() !== code)])];
        cart = await this.request(theme.routes.cart_update_url, {discount: codes.join(','), sections: sectionIds, sections_url: window.location.pathname});
      }
      if (cart.items.some(line => line.properties?._slamit_upsell_id === attemptId)) throw new Error(this.dataset.rollbackError);
      return cart;
    }

    announce(drawer, message) {
      const current = drawer.querySelector('cart-upsell');
      const status = current?.querySelector('[role="status"]') || drawer.querySelector('[data-cart-upsell-status]');
      if (status) status.textContent = message;
    }

    async addToCart() {
      if (this.busy || !this.selected || this.dataset.configured !== 'true') {
        return;
      }
      const drawer = this.closest('cart-drawer');
      if (!drawer || drawer.dataset.cartUpsellPending === 'true') {
        return;
      }
      drawer.dataset.cartUpsellPending = 'true';
      this.busy = true;
      const button = this.querySelector('[data-add]');
      const status = this.querySelector('[role="status"]');
      const checkoutButtons = Array.from(drawer.querySelectorAll('[name="checkout"]')).map(element => ({element, disabled: element.disabled}));
      checkoutButtons.forEach(({element}) => { element.disabled = true; });
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      status.textContent = this.dataset.loading;
      const sectionIds = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', {bubbles: true, detail: {sections: sectionIds}}));
      const attemptId = crypto.randomUUID();
      let attempted = false;
      let verified = false;
      let unsafe = false;
      let discountChanged = false;
      let previousCodes = [];
      try {
        const cartItems = drawer.querySelector('cart-items');
        await cartItems?.debouncedOnChange?.flush?.();
        if (cartItems?.pendingUpdate) await cartItems.pendingUpdate;
        const before = await this.request(theme.routes.cart_url);
        if (this.hasUpsell(before)) {
          await this.refreshCart(before, sectionIds);
          this.announce(drawer, this.dataset.alreadyAdded);
          return;
        }
        if (!before.items.length) throw new Error(this.dataset.error);
        previousCodes = this.codes(before);
        attempted = true;
        const result = await this.request(theme.routes.cart_add_url, {items: [{id: this.selected.dataset.variantId, quantity: 1,
          properties: {_slamit_upsell: 'drawer', _slamit_upsell_id: attemptId}}]});
        let cart;
        if (Number(this.dataset.percentage) > 0) {
          discountChanged = true;
          const codes = [...new Set([...previousCodes, this.dataset.discountCode])];
          cart = await this.request(theme.routes.cart_update_url, {discount: codes.join(','), sections: sectionIds, sections_url: window.location.pathname});
        } else cart = await this.request(theme.routes.cart_url);
        if (!this.discountVerified(cart, attemptId, this.dataset.discountCode, before)) throw new Error(this.dataset.discountError);
        verified = true;
        await this.refreshCart(cart, sectionIds);
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', {detail: {product: result}}));
        this.announce(drawer, this.dataset.success);
        const focusTarget = drawer.querySelector('[name="checkout"]') || drawer.querySelector('.drawer__tab');
        if (focusTarget) {
          theme.a11y.trapFocus(drawer, focusTarget);
          focusTarget.focus({preventScroll: true});
        }
      } catch (error) {
        let message = verified ? this.dataset.refreshError : (error.message || this.dataset.error);
        if (attempted && !verified) {
          try {
            const cart = await this.rollback(attemptId, discountChanged, previousCodes, sectionIds);
            await this.refreshCart(cart, sectionIds);
          } catch (rollbackError) {
            unsafe = true; message = this.dataset.rollbackError;
          }
        }
        status.textContent = message;
        this.announce(drawer, message);
        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartError, {source: 'cart-upsell', errors: message});
      } finally {
        this.busy = false;
        delete drawer.dataset.cartUpsellPending;
        button.removeAttribute('aria-busy');
        button.disabled = verified || unsafe || !this.selected || this.dataset.configured !== 'true';
        if (unsafe) {
          drawer.dataset.upsellUnconfirmed = 'true';
          drawer.querySelectorAll('[name="checkout"]').forEach(element => { element.disabled = true; });
        }
        if (!unsafe) checkoutButtons.forEach(({element, disabled}) => { element.disabled = disabled; });
      }
    }
  });
}
